import * as React from 'react';
import PropTypes from 'prop-types';

import { STATUS } from '../utils/utils';
import {
  setToken,
  clearToken,
  setStoredRefreshToken,
  clearStoredRefreshToken,
} from '../utils/auth';

const initialState = {
  user: {
    profile: {},
    isAdmin: false,
  },
  token: null,
  expiresAt: null,
  isAuthenticated: false,
  status: STATUS.PENDING,
};

const AuthContext = React.createContext({
  ...initialState,
  // eslint-disable-next-line no-unused-vars
  login: (user = {}, token = '', refreshToken = '', expiresAt = '') => {},
  logout: () => {},
  updateUser: () => {},
  setAuthenticationStatus: () => {},
});

const authReducer = (state, action) => {
  switch (action.type) {
    case 'login': {
      return {
        user: action.payload.user,
        token: action.payload.token,
        expiresAt: action.payload.expiresAt,
        isAuthenticated: true,
        verifyingToken: false,
        status: STATUS.SUCCEEDED,
      };
    }
    case 'logout': {
      return {
        ...initialState,
        status: STATUS.IDLE,
      };
    }
    case 'updateUser': {
      return {
        ...state,
        user: action.payload.user,
      };
    }
    case 'status': {
      return {
        ...state,
        status: action.payload.status,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const AuthProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(authReducer, initialState);
  const syncNativeSession = React.useCallback((accessToken, refreshToken) => {
    if (
      typeof window === 'undefined' ||
      !window.LigaAndroid ||
      typeof window.LigaAndroid.syncSession !== 'function'
    ) {
      return;
    }

    try {
      window.LigaAndroid.syncSession(accessToken || '', refreshToken || '');
    } catch (_) {
      /* sincronizacao com a casca em melhor esforco */
    }
  }, []);
  const login = React.useCallback(
    (user, token, refreshTokenOrExpiresAt, maybeExpiresAt) => {
      const hasRefreshToken = Boolean(maybeExpiresAt);
      const refreshToken = hasRefreshToken ? refreshTokenOrExpiresAt : '';
      const expiresAt = hasRefreshToken
        ? maybeExpiresAt
        : refreshTokenOrExpiresAt;

      dispatch({
        type: 'login',
        payload: {
          user,
          token,
          expiresAt,
        },
      });
      setToken(token);
      setStoredRefreshToken(refreshToken);
      syncNativeSession(token, refreshToken);
    },
    [syncNativeSession]
  );
  const logout = React.useCallback(() => {
    clearToken();
    clearStoredRefreshToken();
    if (
      typeof window !== 'undefined' &&
      window.LigaAndroid &&
      typeof window.LigaAndroid.onLogout === 'function'
    ) {
      try {
        window.LigaAndroid.onLogout();
      } catch (_) {
        /* sinaliza app casca no melhor esforco */
      }
    }
    dispatch({
      type: 'logout',
    });
  }, []);
  const updateUser = React.useCallback((user) => {
    dispatch({
      type: 'updateUser',
      payload: {
        user,
      },
    });
  }, []);
  const setAuthenticationStatus = React.useCallback((status) => {
    dispatch({
      type: 'status',
      payload: {
        status,
      },
    });
    clearToken();
    clearStoredRefreshToken();
  }, []);
  const value = React.useMemo(
    () => ({ ...state, login, logout, updateUser, setAuthenticationStatus }),
    [state, setAuthenticationStatus, login, logout, updateUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export { AuthProvider, useAuth };
